import React from 'react';
import { cn } from './cn';

/**
 * Botão do design system Atmos.
 *
 * API previsível, igual em toda a biblioteca: `variant`, `size`, ícone à
 * esquerda/direita e os props nativos. Tamanhos resolvem sempre para
 * 28 / 34 / 40px.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ControlSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'border-0 bg-primary-solid text-white shadow-xs hover:bg-primary-solid-hover',
  secondary:
    'border border-line bg-surface text-fg shadow-xs hover:bg-hover active:bg-active',
  outline:
    'border border-primary-line bg-transparent text-primary hover:bg-primary-soft',
  ghost: 'border-0 bg-transparent text-fg-2 hover:bg-hover hover:text-fg',
  danger: 'border-0 bg-danger-solid text-white shadow-xs hover:brightness-95',
};

/** Alturas do Atmos: sm 28 · md 34 · lg 40. */
export const SIZE: Record<ControlSize, string> = {
  sm: 'h-7 px-[11px] text-[12.5px] gap-1.5 rounded-[7px]',
  md: 'h-[34px] px-[14px] text-[13.5px] gap-2 rounded-lg',
  lg: 'h-10 px-[18px] text-[14.5px] gap-2 rounded-[9px]',
};

/** Anel de foco único do sistema — mesmo token em todo controle. */
export const FOCUS_RING =
  'outline-none focus-visible:border-primary focus-visible:shadow-[0_0_0_3px_var(--ring)]';

const BASE =
  'inline-flex items-center justify-center font-medium whitespace-nowrap transition-[background-color,border-color,box-shadow,filter] duration-[120ms] ' +
  'disabled:cursor-not-allowed disabled:border-line-subtle disabled:bg-sunken disabled:text-faint disabled:shadow-none disabled:hover:bg-sunken';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  /** Mostra spinner e bloqueia o clique, preservando a largura do rótulo. */
  loading?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leadingIcon,
  trailingIcon,
  fullWidth,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(
        BASE,
        VARIANT[variant],
        SIZE[size],
        FOCUS_RING,
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <ButtonSpinner /> : leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}

function ButtonSpinner() {
  return (
    <span className="h-[13px] w-[13px] flex-none animate-spin rounded-full border-2 border-current/40 border-t-current" />
  );
}

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  /** Obrigatório: botão só com ícone precisa de nome acessível. */
  label: string;
}

const ICON_SIZE: Record<ControlSize, string> = {
  sm: 'h-7 w-7 rounded-[7px]',
  md: 'h-[34px] w-[34px] rounded-lg',
  lg: 'h-10 w-10 rounded-[9px]',
};

export function IconButton({
  variant = 'secondary',
  size = 'md',
  label,
  className,
  children,
  type = 'button',
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        BASE,
        'grid place-items-center p-0',
        VARIANT[variant],
        ICON_SIZE[size],
        FOCUS_RING,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Grupo segmentado — os botões se encostam e dividem uma borda só.
 * Use para alternar visões (dia/semana/mês), não para ações.
 */
export function ButtonGroup({
  options,
  value,
  onChange,
  size = 'md',
  className,
}: {
  options: Array<{ value: string; label: React.ReactNode }>;
  value: string;
  onChange: (value: string) => void;
  size?: ControlSize;
  className?: string;
}) {
  const height = size === 'sm' ? 'h-7 px-3 text-[12.5px]' : 'h-[34px] px-3 text-[13.5px]';
  return (
    <div className={cn('inline-flex shadow-xs', className)}>
      {options.map((o, i) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'border border-line transition-colors duration-[120ms]',
              height,
              FOCUS_RING,
              i === 0 ? 'rounded-l-lg' : '-ml-px',
              i === options.length - 1 && 'rounded-r-lg',
              active ? 'bg-active font-semibold text-fg' : 'bg-surface text-fg-2 hover:bg-hover',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
